import '../../modules/mode-components';

import { Compiler } from '@mode-switch/adapt';
import { Attr, Model } from '@mode-switch/model';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { NgxCookieService } from '@mode/shared/data-access-webapp';
import { StringHelpers } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService, CALC_EDITOR_ERROR, RunMethodType } from '@mode/shared/util-segment';
import { Switch } from '@mode/shared/util-switch';
import * as angular from 'angular';
import { Subscription, combineLatest } from 'rxjs';
import { getIncompleteCalc } from '~/lib/calc-field-helpers';
import { CalcFieldsService } from '~/services/calc-fields-service';
import { AppStore } from '~/types/main.types';
import { OnChangeEvent, OnCompileEvent } from './formula-editor-events';

const nonUniqueNameErrorMessage = 'This name is already in use by a column or field in this dataset.';
const maxNameLength = 64;
const nameTooLongErrorMessage = `Maximum length is ${maxNameLength} characters`;

class CalcEditorController implements angular.IController {
  static $inject = ['RxStoreService', 'AnalyticsService', 'CookieService', 'CalcFieldsService'];

  public description: string;
  // Event Bindings
  public onClose: () => void;
  public isExploring = false;
  public isExploration = false;
  public formulaSignature = '';

  // Observable Derived State
  private view: FlamingoTypes.ModeView;
  private calc: FlamingoTypes.ModeCalc;
  private model: FlamingoTypes.ModeModel;
  private switchViewToken: string;
  private isImportedDataset: boolean;
  private attribute: FlamingoTypes.ModeAttr;

  // Local UI State
  private isValidFormula: boolean;
  private hasFormulaResult: boolean;
  private errorMessage: string;
  private isReferenceOpen = false;
  private isNewCalcField: boolean;
  private showRenamingWarning = false;
  private showNonUniqueNameError = false;
  private showNameTooLongError = false;
  private nonUniqueNameMessage = nonUniqueNameErrorMessage;
  private nameTooLongMessage = nameTooLongErrorMessage;

  private name: string;
  private formula: string; // Curr Formula Value
  private compiledFormula: string; // Last Compiled Formula
  private compilerResult: FlamingoTypes.ModeCompilerResult;

  // Subscriptions
  private state$: Subscription;

  constructor(
    private rxStoreService: AppStore,
    private analyticsService: AnalyticsService,
    private cookieService: NgxCookieService,
    private calcFieldsService: CalcFieldsService
  ) {}

  $onInit() {
    this.state$ = combineLatest([
      this.calcFieldsService.observables.modalState$,
      this.calcFieldsService.observables.model$,
    ]).subscribe(([state, model]) => {
      this.model = model;
      this.calc = state.calc;
      this.view = Model.Views.find(model, state.viewName);
      this.isNewCalcField = state.calc == null || state.duplicate;
      this.switchViewToken = state.switchViewToken;
      this.isImportedDataset = state?.isImportedDataset ?? false;
      this.description = this.calc?.desc ?? '';
      this.attribute = Model.Views.findAttr(model, state.viewName, state.calc?.name);
      if (this.name == null) {
        this.name = this.getInitialName();
      }

      if (this.formula == null) {
        this.formula = this.getOrigFormula();
      }
    });

    // check name right away for explorations
    if (this.calc) {
      this.checkName();
    }
  }

  $onChanges(changes: angular.IOnChangesObject) {}

  $onDestroy() {
    this.state$ && this.state$.unsubscribe();
  }

  /**
   * Accessors
   */

  getName() {
    return this.name;
  }

  getFormula() {
    return this.formula;
  }

  getViewName() {
    return this.view?.name;
  }

  getOrigName() {
    return this.calc?.name;
  }

  getOrigDesc() {
    return this.calc?.desc;
  }

  getOrigFormula() {
    return this.calc?.formula.source;
  }

  getInitialName() {
    const origName = this.getOrigName();

    if (origName != null) {
      return origName;
    } else {
      let suffix = 1;
      let propName = `Calculation${suffix}`;

      const viewName = this.getViewName();
      while (Model.Views.findAttr(this.model, viewName, propName)) {
        propName = `Calculation${suffix++}`;
      }

      return propName;
    }
  }

  canSave(): boolean {
    if (this.isNewCalcField) {
      return this.hasValidName() && this.hasValidFormula();
    }

    const changes: boolean[] = [];

    if (this.hasNameUpdate()) {
      changes.push(!!this.hasValidName());
    }

    if (this.hasFormulaUpdate()) {
      changes.push(!!this.hasValidFormula());
    }

    if (changes.length) {
      // Alias for clarity of intent
      const IsValidChange = Boolean;
      return changes.every(IsValidChange);
    }
  }

  hasNameUpdate(): boolean {
    return this.isNewCalcField || this.getName() !== this.getOrigName();
  }

  hasValidName(): boolean {
    return this.nameHasValidLength() && this.nameIsUnique();
  }

  hasFormulaUpdate(): boolean {
    return this.isNewCalcField || this.getFormula() !== this.getOrigFormula();
  }

  hasValidFormula(): boolean {
    return (this.getFormula() ?? '') !== '';
  }

  /**
   * Setters
   */
  updateMessages(result: FlamingoTypes.ModeCompilerResult): void {
    switch (result.type) {
      case Compiler.Result.Type.Success: {
        this.isValidFormula = true;
        break;
      }
      case Compiler.Result.Type.ResolveFailure:
      case Compiler.Result.Type.ParseFailure: {
        this.isValidFormula = false;
        this.errorMessage = StringHelpers.escapeHTML(result.errors[0].message);
        this.trackCalcEditorError(this.errorMessage, result.type);
        break;
      }
    }
  }

  /**
   * Event Handling
   */

  // Bound Events
  onChange($event: OnChangeEvent) {
    this.formula = $event.formula;
    this.hasFormulaResult = this.formula !== '';
  }

  onCompile($event: OnCompileEvent) {
    this.compilerResult = $event.result;
    this.compiledFormula = $event.formula;
    this.hasFormulaResult = this.compilerResult && this.compiledFormula !== '';
    this.updateMessages(this.compilerResult);
  }

  onApply() {
    switch (this.compilerResult.type) {
      case Compiler.Result.Type.Success: {
        // Build Next Calc
        const viewName = this.getViewName();
        const nextName = this.getName();
        const nextFormula = this.getFormula();
        const isNumeric = Switch.Formulas.isNumeric(this.compilerResult.expr.data.type.name);
        // Tableau does not consider the name of the field when deciding whether it's a dimension or measure
        const nextCalc = Switch.calc(
          this.model,
          viewName,
          nextFormula,
          nextName,
          isNumeric ? Attr.Type.Continuous : Attr.Type.Discrete,
          this.description
        );

        if (this.isNewCalcField) {
          this.calcFieldsService.createCalc({
            attr: nextCalc,
            token: this.switchViewToken,
            queryToken: viewName,
          });
        } else {
          const currCalc = this.calc;
          const defaults = Switch.Calcs.defaults(nextCalc.formula.type, currCalc.defaults.aggregate);
          this.calcFieldsService.updateCalc({
            attr: {
              ...nextCalc,
              token: this.attribute?.token,
              defaults,
            },
            prevAttr: currCalc,
            token: this.switchViewToken,
            queryToken: viewName,
          });
        }

        this.trackCalcsCreatedWhileExploring(nextName, nextFormula);
        break;
      }
      default:
        this.saveIncompleteCalc();
        break;
    }
  }

  // When we are unable to compile the given formula, we can leverage the `getIncompleteCalc` helper method to
  // generate the proper data structure to send to webapp to be saved.
  saveIncompleteCalc() {
    const calcName = this.name;
    const calcFormula = this.getFormula();
    const calc = getIncompleteCalc(calcName, calcFormula, this.description);

    if (this.isNewCalcField) {
      this.calcFieldsService.createCalc({
        attr: calc,
        token: this.switchViewToken,
        queryToken: this.getViewName(),
      });
    } else {
      const currCalc = this.calc;
      this.calcFieldsService.updateCalc({
        attr: { ...calc, token: this.attribute?.token },
        prevAttr: currCalc,
        token: this.switchViewToken,
        queryToken: this.getViewName(),
      });
    }

    this.trackCalcsCreatedWhileExploring(calcName, calcFormula, false);
  }

  onSubmit() {
    if (this.canSave()) {
      this.onApply();
    }

    this.onClose();
  }

  nameIsUnique(): boolean {
    const viewName = this.getViewName();
    let isUnique = false;

    if (!this.hasNameUpdate() && !this.calc.isLocal) {
      return true;
    }

    if (this.model != null && viewName != null) {
      const calcAttrs = Switch.Attrs.getCalcs(this.model, viewName);
      const foundAttr = Model.Views.findAttr(this.model, viewName, this.getName()) as FlamingoTypes.ModeAttr;

      if (this.calc?.isLocal) {
        const numCalcDuplicates = calcAttrs.filter((attr) => attr.name === this.getName()).length;
        isUnique = foundAttr ? numCalcDuplicates === 1 && this.calc.token === foundAttr.token : numCalcDuplicates <= 1;

        return isUnique;
      }

      isUnique = foundAttr == null;
    }
    return isUnique;
  }

  nameHasValidLength(): boolean {
    const currentFieldName = this.getName();
    return currentFieldName && currentFieldName.length <= maxNameLength;
  }

  nameExceedsMaxLength(): boolean {
    const currentFieldName = this.getName();
    return currentFieldName != null && currentFieldName.length > maxNameLength;
  }

  checkName(): void {
    this.showRenamingWarning = this.hasNameUpdate() && !this.isNewCalcField;
    this.showNonUniqueNameError = !this.nameIsUnique();
    this.showNameTooLongError = this.nameExceedsMaxLength();
  }

  getTooltipHtml() {
    return `
      <p class="tooltip-error-message">${this.errorMessage}</p>
    `;
  }

  toggleReferenceOpen() {
    this.isReferenceOpen = !this.isReferenceOpen;
  }

  // Tracking

  trackCalcEditorError(errorMessage: string, errorType: string) {
    this.analyticsService.run(RunMethodType.Track, CALC_EDITOR_ERROR, {
      errorMessage,
      errorType,
    });
  }

  trackCalcsCreatedWhileExploring(calc_field_name: string, calc_field_formula: string, is_valid_formula = true) {
    if (this.isExploring) {
      this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.CalcFieldCreatedWhileExploring, {
        calc_field_name,
        calc_field_formula,
        is_valid_formula,
      });
    }
  }

  insertFunction(signature: string) {
    this.formulaSignature = signature;
  }
}

const CalcEditorComponent: angular.IComponentOptions = {
  bindings: {
    onClose: '&',
    isExploring: '<?',
    isExploration: '<?',
  },
  template: `
  <div class="modal-wrapper">
    <div class="modal-calc-editor">
      <div class="modal-body">
        <form class="modal-form">
          <div class="modal-header form-element calc-name-group is-editable draggable-content">
            <div class="calc-name-input modal-header-input draggable-content">
              <input
                type="text"
                class="form-control"
                ng-model="ctrl.name"
                ng-keyup="ctrl.checkName()"
                ng-class="{'error': ctrl.showNonUniqueNameError || ctrl.showNameTooLongError}"/>
                <div ng-click="ctrl.onClose()" class="modal-close">
                  <capra-icon icon="icon-close"></capra-icon>
                </div>
            </div>
            <div class="header-messages">
              <span class="error-message" ng-if="ctrl.showNonUniqueNameError">{{ ctrl.nonUniqueNameMessage }}</span>
              <span class="error-message" ng-if="ctrl.showNameTooLongError">{{ ctrl.nameTooLongMessage }}</span>
            </div>
          </div>

          <div class="form-element formula-editor-group">
            <formula-editor
              formula-signature="ctrl.formulaSignature"
              calc="ctrl.calc"
              model="ctrl.model"
              view-name="ctrl.view.name"
              on-change="ctrl.onChange($event)"
              on-compile="ctrl.onCompile($event)"
              show-renaming-warning="ctrl.showRenamingWarning"
              is-exploreish="ctrl.isExploring || ctrl.isExploration"
              is-imported-dataset="ctrl.isImportedDataset">
            </formula-editor>
          </div>
        </form>
      </div>
      <div class="modal-action-bar">
        <div class="formula-messages" ng-if="ctrl.hasFormulaResult">
          <p class="status-message" ng-class="ctrl.isValidFormula ? 'valid' : 'invalid'" data-test="formula-validation">
            {{ctrl.isValidFormula ? 'Valid formula' : 'Invalid formula'}}
          </p>
          <i ng-if="!ctrl.isValidFormula"
            class="icon-info sm-icon"
            uib-tooltip-html="ctrl.getTooltipHtml()"
            tooltip-class="tooltip-invalid-formula"
            tooltip-placement="top"></i>
        </div>
        <div class="action-group action-group__done">
          <capra-button
            class="calc-modal-apply-button"
            data-test="calc-modal-apply-button"
            text="Apply"
            variant="secondary"
            color="neutral"
            [disabled]="!ctrl.canSave()"
            (clicked)="ctrl.onApply()"></capra-button>
          <capra-button
            data-test="calc-modal-apply-and-close-button"
            text="Apply and Close"
            [disabled]="!ctrl.canSave()"
            (clicked)="ctrl.onSubmit()"></capra-button>
        </div>
      </div>
    </div>
    <div class="modal-reference-library" ng-class="{'reference-library-open': ctrl.isReferenceOpen}">
      <formula-reference insert-function="ctrl.insertFunction(signature)"></formula-reference>
    </div>
  </div>
  `,
  controller: CalcEditorController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('calcEditor', CalcEditorComponent);
